import { theme } from '@cloudinary/url-gen/actions/effect';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Avatar,
  Typography,
  useTheme,
  Link,
  Container,
  Grid,
  Box,
  TableSortLabel,
} from '@mui/material';
import { DbLeagueTeam } from '@par/shared/types/league';
import React, { useEffect, useState } from 'react';
import { resizeImage } from '../../api/cloudinary';
import { fetchGet } from '@par/shared/api/common';
import { roundToOneDecimal } from '../room/utils';
import { visuallyHidden } from '@mui/utils';

type HeaderFields =
  | 'teamName'
  | 'played'
  | 'wins'
  | 'losses'
  | 'draws'
  | 'average'
  | 'difference'
  | 'points';

interface HeadCell {
  disablePadding: boolean;
  id: HeaderFields;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'teamName',
    numeric: false,
    disablePadding: false,
    label: 'Team',
  },
  {
    id: 'played',
    numeric: true,
    disablePadding: false,
    label: 'Plyd',
  },
  {
    id: 'wins',
    numeric: true,
    disablePadding: false,
    label: 'W',
  },
  {
    id: 'losses',
    numeric: true,
    disablePadding: false,
    label: 'L',
  },
  {
    id: 'draws',
    numeric: true,
    disablePadding: false,
    label: 'D',
  },
  {
    id: 'average',
    numeric: true,
    disablePadding: false,
    label: 'Avg',
  },
  {
    id: 'difference',
    numeric: true,
    disablePadding: false,
    label: 'Diff',
  },
  {
    id: 'points',
    numeric: true,
    disablePadding: false,
    label: 'Pts',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: HeaderFields
  ) => void;
  order: Order;
  orderBy: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

type TeamTable = {
  id: string;
  avatarPublicId: string;
  teamName: string;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  difference: number;
  average: number;
  points: number;
};

export const LeagueStandings = () => {
  const theme = useTheme();
  const [teams, setTeams] = useState<TeamTable[]>();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<HeaderFields>('points');

  useEffect(() => {
    fetchAllTeams();
  }, []);

  const fetchAllTeams = async () => {
    const res = await fetchGet(`/league/teams/all`);
    const formattedTeams = formatTeams(res.teams);
    setTeams(formattedTeams);
  };

  const formatTeams = (teams: DbLeagueTeam[]) => {
    const formattedTeams: TeamTable[] = [];
    for (const team of teams) {
      const obj = {
        id: team._id,
        avatarPublicId: team.avatarPublicId,
        teamName: team.teamName,
        played: team.stats.played,
        wins: team.stats.wins,
        losses: team.stats.losses,
        draws: team.stats.draws,
        difference: team.stats.difference,
        average: team.stats.average,
        points: team.stats.points,
      };
      formattedTeams.push(obj);
    }
    return formattedTeams;
  };

  function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler =
      (property: HeaderFields) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };

    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: HeaderFields
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <Typography variant="h5led" textAlign={'center'} letterSpacing={5}>
            STANDINGS
          </Typography>
        </Grid>
        <Grid item sm={12} md={12}>
          <Typography textAlign={'center'} letterSpacing={5}>
            PILOT SEASON
          </Typography>
        </Grid>
        <Grid item sm={12} md={12}>
          <TableContainer component={Paper} sx={{ maxHeight: 432 }}>
            <Table stickyHeader size="small" aria-label="simple table">
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {teams?.sort(getComparator(order, orderBy)).map((t) => (
                  <TableRow key={t.teamName}>
                    <TableCell>
                      <Stack
                        direction={'row'}
                        spacing={1}
                        justifyContent={'left'}
                      >
                        <Avatar
                          src={resizeImage(t.avatarPublicId, 50, 50)}
                          variant="rounded"
                          sx={{
                            height: 25,
                            width: 25,
                          }}
                        />

                        <Typography
                          sx={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            width: 180,
                          }}
                        >
                          {' '}
                          {t.teamName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.played}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.wins}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.losses}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.draws}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.average > 0 ? '+' : ''}
                      {roundToOneDecimal(t.average)}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.difference > 0 ? '+' : ''}
                      {t.difference}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      {t.points}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Container>
  );
};
